import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './Landing.css';

export default function Landing() {
  const [plans, setPlans] = useState(null);

  useEffect(() => {
    fetch('https://parrotmate.com/api/public/plans')
      .then(res => res.json())
      .then(data => {
        if (data.plans) {
          setPlans(data.plans);
        }
      })
      .catch(err => console.error('Failed to fetch plans:', err));
  }, []);

  // Helper function to get plan data or use fallback
  const getPlan = (index, fallbackData) => {
    if (plans && plans[index]) {
      const plan = plans[index];
      return {
        name: plan.name || fallbackData.name,
        badge: plan.badge || fallbackData.badge,
        monthlyFee: plan.pricing?.monthly_fee ? plan.pricing.monthly_fee / 100 : fallbackData.monthlyFee,
        firstMonthFee: plan.pricing?.first_month_fee ? plan.pricing.first_month_fee / 100 : fallbackData.firstMonthFee,
        extraPhoneRate: plan.pricing?.extra_phone_rate ? plan.pricing.extra_phone_rate / 100 : fallbackData.extraPhoneRate / 100,
        voiceOverageRate: plan.pricing?.voice_overage_rate ? plan.pricing.voice_overage_rate / 100 : fallbackData.voiceOverageRate / 100,
        minutesIncluded: plan.inclusions?.minutes_included || fallbackData.minutesIncluded,
        phoneNumbersIncluded: plan.inclusions?.phone_numbers_included || fallbackData.phoneNumbersIncluded,
        retentionDays: plan.inclusions?.retention_days || fallbackData.retentionDays,
        isPopular: plan.is_popular || fallbackData.isPopular,
        isEnterprise: plan.is_enterprise || fallbackData.isEnterprise,
        id: plan.id || fallbackData.id
      };
    }
    return fallbackData;
  };

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal');
        } else {
          // Remove reveal class when element goes out of view for rolladex effect
          entry.target.classList.remove('reveal');
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '-50px 0px -50px 0px' // Trigger animation when element is more in view
    });
    
    document.querySelectorAll('.feature, .testimonial, .stat, .cta-section, .hero-content, .pricing-card').forEach(el => {
      observer.observe(el);
    });
    
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <header>
        <img src="/logo.svg" alt="ParrotMate Logo" className="logo" />
        <nav>
          <a href="#features">Features</a>
          <a href="#testimonials">Testimonials</a>
          <a href="#pricing">Pricing</a>
          <Link to="/sign-in" className="login-btn">Login</Link>
        </nav>
      </header>
      
      <main>
        <section className="hero">
          <div className="hero-content">
            <h1>Meet ParrotMate — the CRM assistant that talks your customers' language</h1>
            <p className="hero-subtitle">Automate follow-ups, summarize every call, and feed spotless notes straight into your pipeline.</p>
            <div className="hero-features">
              <div className="hero-feature">
                <span className="checkmark">✓</span>
                AI voice with human-level clarity
              </div>
              <div className="hero-feature">
                <span className="checkmark">✓</span>
                Real-time call summaries pushed to your CRM
              </div>
              <div className="hero-feature">
                <span className="checkmark">✓</span>
                Usage-based pricing: pay only for minutes you use
              </div>
            </div>
            <div className="hero-ctas">
              <Link to="/sign-up" className="cta-primary">Get Started</Link>
              <a href="#features" className="cta-secondary">Learn More</a>
            </div>
          </div>
        </section>

        <section className="stats">
          <div className="stats-container">
            <div className="stat">
              <div className="stat-number">10,000+</div>
              <div className="stat-label">Calls Processed</div>
            </div>
            <div className="stat">
              <div className="stat-number">95%</div>
              <div className="stat-label">Accuracy Rate</div>
            </div>
            <div className="stat">
              <div className="stat-number">5hrs</div>
              <div className="stat-label">Saved Per Week</div>
            </div>
            <div className="stat">
              <div className="stat-number">24/7</div>
              <div className="stat-label">Always Available</div>
            </div>
          </div>
        </section>

        <section id="features">
          <h2>Everything you need to supercharge your sales calls</h2>
          <div className="features">
            <div className="feature">
              <div className="feature-icon">🎯</div>
              <h3>AI Voice Calls</h3>
              <p>Engage customers with human-level clarity powered by advanced AI voice technology that adapts to your brand voice.</p>
            </div>
            <div className="feature">
              <div className="feature-icon">⚡</div>
              <h3>Real-Time Summaries</h3>
              <p>Get instant call transcripts and intelligent summaries pushed directly to your CRM as conversations happen.</p>
            </div>
            <div className="feature">
              <div className="feature-icon">🔗</div>
              <h3>Smart CRM Integration</h3>
              <p>Seamlessly connect with your favorite CRM platforms. Notes, follow-ups, and insights flow automatically.</p>
            </div>
            <div className="feature">
              <div className="feature-icon">💰</div>
              <h3>Usage-Based Pricing</h3>
              <p>Scale your costs with your business. Only pay for the minutes you use with transparent, flexible pricing.</p>
            </div>
            <div className="feature">
              <div className="feature-icon">📞</div>
              <h3>Local Phone Numbers</h3>
              <p>Purchase local phone numbers in any area code to build trust and improve connection rates with prospects.</p>
            </div>
            <div className="feature">
              <div className="feature-icon">🧠</div>
              <h3>Custom AI Training</h3>
              <p>Train your AI assistant with your specific knowledge base, scripts, and business processes for perfect alignment.</p>
            </div>
          </div>
        </section>

        <section id="testimonials">
          <h2>Trusted by sales teams everywhere</h2>
          <div className="testimonials">
            <div className="testimonial">
              <div className="testimonial-content">
                <p>"ParrotMate transformed our sales calls. The AI-driven summaries save our team hours every week and our close rate has improved by 40%."</p>
                <div className="author">— Sarah</div>
              </div>
            </div>
            <div className="testimonial">
              <div className="testimonial-content">
                <p>"We've closed deals faster because call notes are instantly available in our CRM. No more manual data entry or missed follow-ups."</p>
                <div className="author">— Marcus</div>
              </div>
            </div>
            <div className="testimonial">
              <div className="testimonial-content">
                <p>"The voice quality is incredible. Our prospects can't tell it's AI, and the conversation flows naturally every time."</p>
                <div className="author">— Jennifer</div>
              </div>
            </div>
            <div className="testimonial">
              <div className="testimonial-content">
                <p>"Setup was incredibly easy. We were making AI-powered calls within 30 minutes of signing up. Game changer for our outbound sales."</p>
                <div className="author">— David</div>
              </div>
            </div>
            <div className="testimonial">
              <div className="testimonial-content">
                <p>"The cost savings are huge. We're reaching 3x more prospects with the same budget, and conversion rates are through the roof."</p>
                <div className="author">— Lisa</div>
              </div>
            </div>
            <div className="testimonial">
              <div className="testimonial-content">
                <p>"Finally, a solution that actually works. The transcriptions are accurate and the CRM integration is seamless."</p>
                <div className="author">— Michael</div>
              </div>
            </div>
            <div className="testimonial">
              <div className="testimonial-content">
                <p>"Our team productivity has skyrocketed. ParrotMate handles the initial outreach so we can focus on closing deals."</p>
                <div className="author">— Amanda</div>
              </div>
            </div>
            <div className="testimonial">
              <div className="testimonial-content">
                <p>"The analytics and insights from call summaries help us understand our prospects better than ever before."</p>
                <div className="author">— Robert</div>
              </div>
            </div>
          </div>
        </section>

        <section id="pricing">
          <h2>Choose the perfect plan for your business</h2>
          <p className="pricing-subtitle">All plans include AI voice calls, real-time summaries, and CRM integration. Built around a 2½-minute average call + one summary message per call.</p>
          
          <div className="pricing-grid">
            {(() => {
              const starterPlan = getPlan(0, {
                name: 'Starter',
                badge: 'Perfect for testing',
                monthlyFee: 1900,
                firstMonthFee: 900,
                extraPhoneRate: 200,
                voiceOverageRate: 35,
                minutesIncluded: 10,
                phoneNumbersIncluded: 1,
                retentionDays: 7,
                isPopular: false,
                isEnterprise: false,
                id: 'starter'
              });

              const growthPlan = getPlan(1, {
                name: 'Growth',
                badge: 'Most Popular',
                monthlyFee: 4900,
                firstMonthFee: 2900,
                extraPhoneRate: 200,
                voiceOverageRate: 30,
                minutesIncluded: 60,
                phoneNumbersIncluded: 1,
                retentionDays: 30,
                isPopular: true,
                isEnterprise: false,
                id: 'growth'
              });

              const professionalPlan = getPlan(2, {
                name: 'Professional',
                badge: 'Best value',
                monthlyFee: 9900,
                firstMonthFee: 6900,
                extraPhoneRate: 200,
                voiceOverageRate: 25,
                minutesIncluded: 180,
                phoneNumbersIncluded: 1,
                retentionDays: 90,
                isPopular: false,
                isEnterprise: false,
                id: 'professional'
              });

              const enterprisePlan = getPlan(3, {
                name: 'Enterprise',
                badge: 'Custom solution',
                monthlyFee: 0,
                firstMonthFee: 0,
                extraPhoneRate: 0,
                voiceOverageRate: 0,
                minutesIncluded: 0,
                phoneNumbersIncluded: 0,
                retentionDays: 0,
                isPopular: false,
                isEnterprise: true,
                id: 'enterprise'
              });

              return (
                <>
                  <div className={`pricing-card ${starterPlan.isPopular ? 'popular' : ''} ${starterPlan.isEnterprise ? 'enterprise' : ''}`}>
                    <div className="pricing-header">
                      <h3>{starterPlan.name}</h3>
                      <div className="pricing-badge">{starterPlan.badge}</div>
                    </div>
                    <div className="pricing-price">
                      <span className="price-main">{starterPlan.isEnterprise ? 'Contact Us' : `$${starterPlan.monthlyFee}`}</span>
                      {!starterPlan.isEnterprise && <span className="price-period">/month</span>}
                    </div>
                    <div className="pricing-promo">{starterPlan.isEnterprise ? 'Custom pricing' : `First Month $${starterPlan.firstMonthFee}`}</div>
                    <ul className="pricing-features">
                      {starterPlan.isEnterprise ? (
                        <>
                          <li><strong>Custom minutes & calls</strong></li>
                          <li><strong>Multiple phone numbers</strong></li>
                          <li><strong>Custom integrations</strong></li>
                          <li><strong>Priority support</strong></li>
                          <li><strong>Extended storage</strong></li>
                        </>
                      ) : (
                        <>
                          <li><strong>{starterPlan.minutesIncluded} minutes</strong> included (≈ {Math.floor(starterPlan.minutesIncluded / 2.5)} calls)</li>
                          <li><strong>{starterPlan.phoneNumbersIncluded} phone number</strong> included</li>
                          <li>Extra phone #: <strong>${starterPlan.extraPhoneRate.toFixed(2)}/month</strong></li>
                          <li>Voice overage: <strong>${starterPlan.voiceOverageRate.toFixed(2)}/minute</strong></li>
                          <li><strong>{starterPlan.retentionDays} days</strong> recording storage</li>
                        </>
                      )}
                    </ul>
                    {starterPlan.isEnterprise ? (
                      <a href="mailto:sales@parrotmate.com" className="pricing-cta">Contact Sales</a>
                    ) : (
                      <Link to={`/sign-up?plan=${starterPlan.id}`} className="pricing-cta">Get Started</Link>
                    )}
                  </div>

                  <div className={`pricing-card ${growthPlan.isPopular ? 'popular' : ''} ${growthPlan.isEnterprise ? 'enterprise' : ''}`}>
                    <div className="pricing-header">
                      <h3>{growthPlan.name}</h3>
                      <div className="pricing-badge">{growthPlan.isPopular ? `${growthPlan.badge}` : `${growthPlan.badge}`}</div>
                    </div>
                    <div className="pricing-price">
                      <span className="price-main">{growthPlan.isEnterprise ? 'Contact Us' : `$${growthPlan.monthlyFee}`}</span>
                      {!growthPlan.isEnterprise && <span className="price-period">/month</span>}
                    </div>
                    <div className="pricing-promo">{growthPlan.isEnterprise ? 'Custom pricing' : `First Month $${growthPlan.firstMonthFee}`}</div>
                    <ul className="pricing-features">
                      {growthPlan.isEnterprise ? (
                        <>
                          <li><strong>Custom minutes & calls</strong></li>
                          <li><strong>Multiple phone numbers</strong></li>
                          <li><strong>Custom integrations</strong></li>
                          <li><strong>Priority support</strong></li>
                          <li><strong>Extended storage</strong></li>
                        </>
                      ) : (
                        <>
                          <li><strong>{growthPlan.minutesIncluded} minutes</strong> included (≈ {Math.floor(growthPlan.minutesIncluded / 2.5)} calls)</li>
                          <li><strong>{growthPlan.phoneNumbersIncluded} phone number</strong> included</li>
                          <li>Extra phone #: <strong>${growthPlan.extraPhoneRate.toFixed(2)}/month</strong></li>
                          <li>Voice overage: <strong>${growthPlan.voiceOverageRate.toFixed(2)}/minute</strong> <span className="promo-text">(Promo)</span></li>
                          <li><strong>{growthPlan.retentionDays} days</strong> recording storage</li>
                        </>
                      )}
                    </ul>
                    {growthPlan.isEnterprise ? (
                      <a href="mailto:sales@parrotmate.com" className="pricing-cta">Contact Sales</a>
                    ) : (
                      <Link to={`/sign-up?plan=${growthPlan.id}`} className="pricing-cta">Get Started</Link>
                    )}
                  </div>

                  <div className={`pricing-card ${professionalPlan.isPopular ? 'popular' : ''} ${professionalPlan.isEnterprise ? 'enterprise' : ''}`}>
                    <div className="pricing-header">
                      <h3>{professionalPlan.name}</h3>
                      <div className="pricing-badge">{professionalPlan.badge}</div>
                    </div>
                    <div className="pricing-price">
                      <span className="price-main">{professionalPlan.isEnterprise ? 'Contact Us' : `$${professionalPlan.monthlyFee}`}</span>
                      {!professionalPlan.isEnterprise && <span className="price-period">/month</span>}
                    </div>
                    <div className="pricing-promo">{professionalPlan.isEnterprise ? 'Custom pricing' : `First Month $${professionalPlan.firstMonthFee}`}</div>
                    <ul className="pricing-features">
                      {professionalPlan.isEnterprise ? (
                        <>
                          <li><strong>Custom minutes & calls</strong></li>
                          <li><strong>Multiple phone numbers</strong></li>
                          <li><strong>Custom integrations</strong></li>
                          <li><strong>Priority support</strong></li>
                          <li><strong>Extended storage</strong></li>
                        </>
                      ) : (
                        <>
                          <li><strong>{professionalPlan.minutesIncluded} minutes</strong> included (≈ {Math.floor(professionalPlan.minutesIncluded / 2.5)} calls)</li>
                          <li><strong>{professionalPlan.phoneNumbersIncluded} phone number</strong> included</li>
                          <li>Extra phone #: <strong>${professionalPlan.extraPhoneRate.toFixed(2)}/month</strong></li>
                          <li>Voice overage: <strong>${professionalPlan.voiceOverageRate.toFixed(2)}/minute</strong> <span className="promo-text">(Promo)</span></li>
                          <li><strong>{professionalPlan.retentionDays} days</strong> recording storage</li>
                        </>
                      )}
                    </ul>
                    {professionalPlan.isEnterprise ? (
                      <a href="mailto:sales@parrotmate.com" className="pricing-cta">Contact Sales</a>
                    ) : (
                      <Link to={`/sign-up?plan=${professionalPlan.id}`} className="pricing-cta">Get Started</Link>
                    )}
                  </div>

                  <div className={`pricing-card ${enterprisePlan.isPopular ? 'popular' : ''} ${enterprisePlan.isEnterprise ? 'enterprise' : ''}`}>
                    <div className="pricing-header">
                      <h3>{enterprisePlan.name}</h3>
                      <div className="pricing-badge">{enterprisePlan.badge}</div>
                    </div>
                    <div className="pricing-price">
                      <span className="price-main">{enterprisePlan.isEnterprise ? 'Contact Us' : `$${enterprisePlan.monthlyFee}`}</span>
                      {!enterprisePlan.isEnterprise && <span className="price-period">/month</span>}
                    </div>
                    <div className="pricing-promo">{enterprisePlan.isEnterprise ? 'Custom pricing' : `First Month $${enterprisePlan.firstMonthFee}`}</div>
                    <ul className="pricing-features">
                      {enterprisePlan.isEnterprise ? (
                        <>
                          <li><strong>Custom minutes & calls</strong></li>
                          <li><strong>Multiple phone numbers</strong></li>
                          <li><strong>Custom integrations</strong></li>
                          <li><strong>Priority support</strong></li>
                          <li><strong>Extended storage</strong></li>
                        </>
                      ) : (
                        <>
                          <li><strong>{enterprisePlan.minutesIncluded} minutes</strong> included (≈ {Math.floor(enterprisePlan.minutesIncluded / 2.5)} calls)</li>
                          <li><strong>{enterprisePlan.phoneNumbersIncluded} phone number</strong> included</li>
                          <li>Extra phone #: <strong>${enterprisePlan.extraPhoneRate.toFixed(2)}/month</strong></li>
                          <li>Voice overage: <strong>${enterprisePlan.voiceOverageRate.toFixed(2)}/minute</strong></li>
                          <li><strong>{enterprisePlan.retentionDays} days</strong> recording storage</li>
                        </>
                      )}
                    </ul>
                    {enterprisePlan.isEnterprise ? (
                      <a href="mailto:sales@parrotmate.com" className="pricing-cta">Contact Sales</a>
                    ) : (
                      <Link to={`/sign-up?plan=${enterprisePlan.id}`} className="pricing-cta">Get Started</Link>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
          
          <div className="pricing-note">
            <p>All prices in USD. No setup fees. Cancel anytime.</p>
          </div>
        </section>

        <section className="cta-section">
          <div className="cta-content">
            <h2>Ready to transform your sales calls?</h2>
            <p>Join thousands of sales teams already using ParrotMate to close more deals faster.</p>
            <div className="cta-buttons">
              <Link to="/sign-up" className="cta-primary large">Get Started</Link>
              <a href="#pricing" className="cta-secondary large">View Pricing</a>
            </div>
            <p className="cta-note">No setup fees • Cancel anytime</p>
          </div>
        </section>
      </main>
      
      <footer>
        <div className="footer-content">
          <div className="footer-section">
            <img src="/logo.svg" alt="ParrotMate Logo" className="footer-logo" />
            <p>The CRM assistant that speaks your customers' language.</p>
          </div>
          <div className="footer-section">
            <h4>Product</h4>
            <a href="#features">Features</a>
            <Link to="/pricing">Pricing</Link>
            <a href="#testimonials">Testimonials</a>
          </div>
          <div className="footer-section">
            <h4>Company</h4>
            <Link to="/sign-in">Login</Link>
            <Link to="/sign-up">Sign Up</Link>
          </div>
        </div>
        <div className="footer-bottom">
          &copy; {new Date().getFullYear()} ParrotMate. All rights reserved.
        </div>
      </footer>
    </>
  );
}
